import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArticleDetail } from '../components/ArticleDetail'
import { CopyFallbackDialog } from '../components/CopyFallbackDialog'
import { VocabularyDialog } from '../components/VocabularyDialog'
import { setExistingItemInterest, setExistingItemSaved, setItemInterest, setItemSaved } from '../data/db'
import { useItemStatesData } from '../hooks/useLocalData'
import { useVocabularyLookup } from '../hooks/useVocabularyLookup'
import type { InterestValue, ItemReference, ItemState } from '../types/daily'
import { shareDiscussion } from '../utils/discussion'
import { topicLabel } from '../utils/labels'

type LibraryTab = 'saved' | 'feedback' | 'recent'
const tabs: Array<{ id: LibraryTab; label: string }> = [
  { id: 'saved', label: '收藏' },
  { id: 'feedback', label: '反馈' },
  { id: 'recent', label: '最近阅读' },
]

const formatDate = (value?: string) => value ? new Intl.DateTimeFormat('zh-CN', {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Singapore',
}).format(new Date(value)) : '时间未知'

function CompactItem({ state, actions }: { state: ItemState; actions?: React.ReactNode }) {
  const reference = state.reference
  return (
    <article className="library-card">
      <div className="library-card__meta">
        <span>{reference?.source.name ?? '来源未知'}</span>
        <span>{formatDate(state.lastOpenedAt ?? state.updatedAt)}</span>
      </div>
      <h3 lang={reference?.source.language === 'en' ? 'en' : 'zh-CN'}>{reference?.title ?? `旧记录 ${state.itemId}`}</h3>
      {reference ? <p>{reference.summary}</p> : <p className="muted-copy">这是一条旧版记录，内容引用尚不可用。</p>}
      {reference && <div className="topic-row">{reference.topics.map((topic) => <span key={topic}>{topicLabel(topic)}</span>)}</div>}
      <div className="compact-actions">
        {reference && <a href={reference.source.url} target="_blank" rel="noreferrer noopener">打开原文 ↗</a>}
        {actions}
      </div>
    </article>
  )
}

export function LibraryPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: states, loading, error, refresh } = useItemStatesData()
  const tab = (tabs.some((item) => item.id === searchParams.get('tab')) ? searchParams.get('tab') : 'saved') as LibraryTab
  const activeKey = searchParams.get('item')
  const [query, setQuery] = useState('')
  const [feedbackFilter, setFeedbackFilter] = useState<'all' | 'interested' | 'not_interested'>('all')
  const [recentLimit, setRecentLimit] = useState(20)
  const vocabularyLookup = useVocabularyLookup()
  const [copyFallback, setCopyFallback] = useState<string>()
  const [toast, setToast] = useState<string>()

  useEffect(() => {
    if (!toast) return
    const timeout = window.setTimeout(() => setToast(undefined), 2400)
    return () => window.clearTimeout(timeout)
  }, [toast])

  const saved = useMemo(() => states
    .filter((state) => state.saved)
    .sort((left, right) => (right.savedAt ?? right.updatedAt).localeCompare(left.savedAt ?? left.updatedAt)), [states])
  const filteredSaved = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase()
    if (!normalized) return saved
    return saved.filter((state) => {
      const reference = state.reference
      return [reference?.title, reference?.source.name, ...(reference?.topics ?? [])].some((value) => value?.toLocaleLowerCase().includes(normalized))
    })
  }, [query, saved])
  const feedback = useMemo(() => states
    .filter((state) => state.interest && (feedbackFilter === 'all' || state.interest === feedbackFilter))
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)), [feedbackFilter, states])
  const recent = useMemo(() => states
    .filter((state) => state.lastOpenedAt)
    .sort((left, right) => (right.lastOpenedAt ?? '').localeCompare(left.lastOpenedAt ?? '')), [states])
  const setTab = (next: LibraryTab) => {
    const params = new URLSearchParams()
    params.set('tab', next)
    setSearchParams(params)
  }
  const setActive = (key?: string) => {
    const params = new URLSearchParams(searchParams)
    if (key) params.set('item', key)
    else params.delete('item')
    setSearchParams(params)
  }
  const discuss = async (item: ItemReference | NonNullable<ItemState['savedSnapshot']>) => {
    const result = await shareDiscussion(item)
    if (result.status === 'shared') setToast('已打开系统分享')
    if (result.status === 'copied') setToast('讨论上下文已复制')
    if (result.status === 'manual') setCopyFallback(result.value)
  }
  const updateFeedback = async (state: ItemState, interest: InterestValue) => {
    await setExistingItemInterest(state.key, interest)
    setToast(interest ? '反馈已更新' : '反馈已撤销')
  }

  if (loading) return <section className="loading-state" aria-label="正在读取资料库"><span /><span /><span /></section>
  if (error) return <section className="empty-state page-enter"><p className="eyebrow">Reading library</p><h1>暂时无法打开资料库</h1><p>请重试；你的收藏和阅读记录不会因此丢失。</p><button className="primary-button" type="button" onClick={() => void refresh()}>重试</button></section>

  return (
    <div className="local-page page-enter">
      <header className="local-hero"><p className="eyebrow">Reading library</p><h1>资料库</h1><p>把想再看的文章、你的偏好和最近读过的内容放在一起；收藏后即使离线也能重读。</p></header>
      <div className="segmented-tabs" role="tablist" aria-label="资料库分栏">
        {tabs.map((item) => <button key={item.id} id={`library-tab-${item.id}`} aria-controls={`library-panel-${item.id}`} type="button" role="tab" aria-selected={tab === item.id} className={tab === item.id ? 'is-active' : ''} onClick={() => setTab(item.id)}>{item.label}</button>)}
      </div>

      {tab === 'saved' && <section id="library-panel-saved" role="tabpanel" aria-labelledby="library-tab-saved">
        <label className="search-field"><span>搜索收藏</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="标题、来源或主题" type="search" /></label>
        {filteredSaved.length === 0 && <div className="inline-empty"><h2>{saved.length ? '没有匹配的收藏' : '还没有收藏'}</h2><p>在今日页展开文章并点击“收藏”，以后就能在这里重读，离线时也可以打开。</p></div>}
        <div className="library-list">{filteredSaved.map((state) => {
          const isOpen = activeKey === state.key
          return <div key={state.key} className="library-entry">
            <CompactItem state={state} actions={<>
              {state.savedSnapshot && <button type="button" aria-expanded={isOpen} onClick={() => setActive(isOpen ? undefined : state.key)}>{isOpen ? '收起' : '展开阅读'}</button>}
              {!state.savedSnapshot && <button type="button" onClick={() => void setExistingItemSaved(state.key, false)}>取消收藏</button>}
            </>} />
            {!state.savedSnapshot && <p className="snapshot-warning" role="status">这条较早的收藏没有保留可离线阅读的内容。你仍可打开原文或取消收藏。</p>}
            {isOpen && state.savedSnapshot && <ArticleDetail
              item={state.savedSnapshot}
              state={state}
              onSaved={(value) => void setItemSaved(state.issueId, state.issueDate ?? state.reference?.issueDate ?? '', state.savedSnapshot!, value)}
              onInterest={(value) => void setItemInterest(state.issueId, state.issueDate ?? state.reference?.issueDate ?? '', state.savedSnapshot!, value)}
              onVocabulary={(entry) => void vocabularyLookup.openCurated(state.issueId, state.issueDate ?? state.reference?.issueDate ?? '', state.savedSnapshot!, entry)}
              onLookupSelection={(selection) => void vocabularyLookup.lookupSelection(state.issueId, state.issueDate ?? state.reference?.issueDate ?? '', state.savedSnapshot!, selection)}
              onDiscuss={() => void discuss(state.savedSnapshot!)}
            />}
          </div>
        })}</div>
      </section>}

      {tab === 'feedback' && <section id="library-panel-feedback" role="tabpanel" aria-labelledby="library-tab-feedback">
        <div className="filter-row" aria-label="反馈筛选">
          {(['all', 'interested', 'not_interested'] as const).map((value) => <button key={value} type="button" className={feedbackFilter === value ? 'is-active' : ''} onClick={() => setFeedbackFilter(value)}>{value === 'all' ? '全部' : value === 'interested' ? '感兴趣' : '不感兴趣'}</button>)}
        </div>
        {feedback.length === 0 && <div className="inline-empty"><h2>暂无反馈记录</h2><p>你可以在文章详情中记录感兴趣或不感兴趣。</p></div>}
        <div className="library-list">{feedback.map((state) => <CompactItem key={state.key} state={state} actions={<>
          <button type="button" className={state.interest === 'interested' ? 'is-active' : ''} onClick={() => void updateFeedback(state, state.interest === 'interested' ? null : 'interested')}>感兴趣</button>
          <button type="button" className={state.interest === 'not_interested' ? 'is-negative' : ''} onClick={() => void updateFeedback(state, state.interest === 'not_interested' ? null : 'not_interested')}>不感兴趣</button>
          {state.reference && <button type="button" onClick={() => void discuss(state.reference!)}>与 Codex 讨论</button>}
        </>} />)}</div>
      </section>}

      {tab === 'recent' && <section id="library-panel-recent" role="tabpanel" aria-labelledby="library-tab-recent">
        {recent.length === 0 && <div className="inline-empty"><h2>还没有阅读足迹</h2><p>从今日页展开文章后，这里会按最近打开时间排序。</p></div>}
        <div className="library-list">{recent.slice(0, recentLimit).map((state) => <CompactItem key={state.key} state={state} actions={state.reference && <button type="button" onClick={() => void discuss(state.reference!)}>与 Codex 讨论</button>} />)}</div>
        {recentLimit < recent.length && <button type="button" className="load-more" onClick={() => setRecentLimit((value) => value + 20)}>继续加载 20 条</button>}
      </section>}

      <VocabularyDialog
        lookup={vocabularyLookup.lookup}
        onClose={vocabularyLookup.close}
        onToggleSaved={(value) => void vocabularyLookup.toggleSaved(value).then(() => setToast(value ? '表达已收藏' : '已取消表达收藏'))}
        onSavePending={() => void vocabularyLookup.savePending().then((result) => result && setToast('已保存到词汇本，等待补充释义'))}
        onRetry={() => void vocabularyLookup.retry()}
      />
      <CopyFallbackDialog value={copyFallback} onClose={() => setCopyFallback(undefined)} />
      {toast && <div className="action-toast" role="status">{toast}</div>}
    </div>
  )
}
